import { NavPage } from '@/components/page/Breadcrumbs';
import { PageContainer } from '@/components/page/PageContainer';
import { PageHeader } from '@/components/page/PageHeader';
import { Button } from '@/components/shared/button/Button';
import { Card } from '@/components/shared/card/Card';
import { Loading } from '@/components/shared/Loading';
import { handleMutationResult } from '@/helpers/toastHelper';
import { useConfirm } from '@/services/confirm/ConfirmationService';
import { ReadTemplate, useGetTemplateQuery, useUpdateTemplateMutation } from '@/services/gql/graphql.generated';
import { strings } from '@/services/translation/strings';
import { WorkingRoster } from '@/types/roster.types';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { FiCalendar } from 'react-icons/fi';
import { useParams } from 'react-router-dom';
import { TemplateEditor } from './TemplateEditor';
import { buildTemplateUpdate, useGqlTemplateToWorkingTemplate } from './templateHelper';

const formatTemplateLabel = (template: ReadTemplate) => {
  const targetType = template.target?.type;
  return targetType ? `${template.name} (${targetType})` : template.name;
};

export const TemplateEditorPage: FC = () => {
  const { templateId } = useParams<{ templateId: string }>();

  const [templateResult, reexecuteTemplate] = useGetTemplateQuery({
    variables: { id: templateId ?? '' },
    pause: !templateId,
    requestPolicy: 'network-only'
  });

  const [{ fetching: saving }, updateTemplate] = useUpdateTemplateMutation();
  const gqlTemplateToWorkingTemplate = useGqlTemplateToWorkingTemplate();
  const [workingTemplate, setWorkingTemplateState] = useState<WorkingRoster | undefined>(undefined);

  const { registerForConfirmation, unregisterForConfirmation, saveNeeded, registerAction } = useConfirm(state => ({
    registerForConfirmation: state.registerForConfirmation,
    unregisterForConfirmation: state.unregisterForConfirmation,
    saveNeeded: state.confirmationNeeded,
    registerAction: state.registerConfirmAction
  }));

  const selectedTemplate = useMemo(
    () => (templateResult.data?.template as ReadTemplate) ?? undefined,
    [templateResult.data?.template]
  );

  useEffect(() => {
    if (selectedTemplate) {
      setWorkingTemplateState(gqlTemplateToWorkingTemplate(selectedTemplate));
      unregisterForConfirmation();
    }
  }, [gqlTemplateToWorkingTemplate, selectedTemplate, unregisterForConfirmation]);

  const setWorkingTemplate = useCallback(
    (template: WorkingRoster) => {
      registerForConfirmation(strings.daily.roster.unsaved, strings.daily.roster.leaveWithoutSaving);
      setWorkingTemplateState(template);
    },
    [registerForConfirmation]
  );

  const saveTemplate = useCallback(async () => {
    if (!selectedTemplate || !workingTemplate) {
      throw Error('Template must be selected before saving');
    }

    const result = await updateTemplate({
      updateTemplate: buildTemplateUpdate(workingTemplate, selectedTemplate)
    });

    await handleMutationResult(result, {
      successMessage: 'Template saved successfully!',
      errorMessage: 'Error!',
      onSuccess: async data => {
        if (!data?.updateTemplate) return;

        await reexecuteTemplate({ requestPolicy: 'network-only' });
        unregisterForConfirmation();
      }
    });

    return result;
  }, [reexecuteTemplate, selectedTemplate, unregisterForConfirmation, updateTemplate, workingTemplate]);

  useEffect(() => {
    if (saveNeeded) {
      registerAction({
        callback: saveTemplate,
        label: strings.daily.roster.saveAndLeave
      });
    }
  }, [registerAction, saveNeeded, saveTemplate]);

  const breadcrumbs: NavPage[] = useMemo(
    () => [
      {
        name: 'Templates',
        to: '/templates'
      },
      ...(selectedTemplate
        ? [
            {
              name: formatTemplateLabel(selectedTemplate),
              to: `/templates/${selectedTemplate.id}`
            }
          ]
        : [])
    ],
    [selectedTemplate]
  );

  if (!templateId) {
    return (
      <PageContainer fullWidth>
        <Card className="p-6">No template selected.</Card>
      </PageContainer>
    );
  }

  if (!templateResult.data && !templateResult.error) {
    return <Loading />;
  }

  return (
    <PageContainer
      lockHeader
      fullWidth
      customHeader={
        <PageHeader
          fullWidth
          pageTitle={selectedTemplate ? formatTemplateLabel(selectedTemplate) : 'Template'}
          icon={<FiCalendar />}
          breadcrumbs={breadcrumbs}
        >
          <div className="flex gap-3 items-center">
            <Button primary disabled={!saveNeeded} loading={saving} onClick={saveTemplate}>
              {strings.common.save}
            </Button>
          </div>
        </PageHeader>
      }
    >
      <div className="h-full flex flex-col flex-1 min-h-0">
        <div className="flex-1 min-w-0 flex flex-col min-h-0">
          {selectedTemplate && workingTemplate ? (
            <TemplateEditor workingTemplate={workingTemplate} setWorkingTemplate={setWorkingTemplate} />
          ) : (
            <Card className="p-6">No template selected.</Card>
          )}
        </div>
      </div>
    </PageContainer>
  );
};
